import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHead, TableHeader, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { cn } from "@/lib/utils";

/**
 * Generic data table wrapper used by all module pages.
 * Provides search + column rendering + empty state + loading state.
 */
export default function DataTable({
  endpoint,
  data,
  columns,
  searchPlaceholder = "Search…",
  emptyText = "No records yet.",
  testId,
  refreshKey,
  rowKey = "id",
  onRowClick,
  toolbar,
  filterFn,
}) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    // If `data` is supplied externally, skip the internal fetch — the parent
    // owns the data source. Otherwise hit the endpoint as before.
    if (data !== undefined) {
      setRows(data);
      return undefined;
    }
    if (!endpoint) return undefined;
    let mounted = true;
    api.get(endpoint).then((res) => {
      if (mounted) setRows(res.data);
    });
    return () => { mounted = false; };
  }, [endpoint, refreshKey, data]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    let result = rows;
    if (typeof filterFn === "function") {
      result = result.filter(filterFn);
    }
    if (q) {
      const ql = q.toLowerCase();
      result = result.filter((r) => JSON.stringify(r).toLowerCase().includes(ql));
    }
    return result;
  }, [rows, q, filterFn]);

  return (
    <Card className="hr-card overflow-hidden" data-testid={testId}>
      <div className="p-4 border-b border-gray-100 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid={`${testId}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 h-9"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">{toolbar}</div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/60">
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn("hr-table-head py-3 px-4", c.align === "right" && "text-right")}
                >
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered === null && (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className="py-3 px-4"><Skeleton className="h-4 w-24" /></TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {filtered && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-10 text-sm text-gray-500">
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
            {filtered && filtered.map((row, idx) => (
              <TableRow
                key={row[rowKey] || idx}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn("hover:bg-gray-50 transition", onRowClick && "cursor-pointer")}
                data-testid={`${testId}-row-${idx}`}
              >
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn("py-3 px-4 text-sm text-gray-700", c.align === "right" && "text-right", c.mono && "font-mono")}
                  >
                    {c.render ? c.render(row, idx) :
                      c.badge ? <StatusBadge status={row[c.key]} /> :
                      row[c.key] ?? "—"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export { Button };
